import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  FormFeedback,
  Button,
  Alert,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import logo from "../../assets/images/Elebert-Logo.png";
import background from "../../assets/images/background.svg";
import logo360 from "../../assets/images/360-degrees 1.svg";
import { post } from "helpers/api_helper";
import * as api from "../../constants/api_constants";
import { BsEye, BsEyeSlash } from "react-icons/bs"; // Bootstrap icons for eye and eye-slash

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showNewPassword, setShowNewPassword] = useState(false); // State for new password visibility
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // State for confirm password visibility

  const validation = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .required("Please Enter Your New Password"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
        .required("Please Confirm Your Password"),
    }),
    onSubmit: async (values) => {
      setError(null);
      setSuccess(null);

      try {
        const payload = {
          email: email,
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        };
        const response = await post(api.POST_RESET_PASSWORD, payload);
        setSuccess("Password reset successfully!");
        setTimeout(() => navigate("/login"), 2000);
      } catch (error) {
        setError(
          error.response?.data?.message || "Failed to reset password. Please try again."
        );
        console.error("Error resetting password:", error);
      }
    },
  });

  document.title = "Reset Password | Elbert";

  return (
    <React.Fragment>
      <div className="account-pages d-flex flex-row h-100">
        <div
          className="px-5 py-4 d-flex flex-column justify-content-between login-banner d-sm-block d-none"
          style={{
            backgroundImage: `url(${background})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
            width: "85%",
          }}
        >
          <div className="w-75 h-50 d-flex flex-column justify-content-center align-items-start text-white">
            <h1 className="my-5 d-flex justify-content-start">
              Engage <img src={logo360} alt="logo 360" />
            </h1>
            <h1 style={{ color: "white", marginBottom: "20px" }}>
              Power Up Your Day with Engage 360
            </h1>
            <p className="text-white">
              A Smarter Where Customer Relationships Are Not Just Managed, but
              Transformed into Meaningful Connections That Drive Success
            </p>
          </div>
          <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ marginTop: "240px" }}
          >
            <p className="text-white">Powered by Elbert Technology</p>
          </div>
        </div>
        <Container className="d-flex flex-column align-items-center justify-content-center">
          <Row className="w-50">
            <Col md={12}>
              <Card className="overflow-hidden p-0 d-flex flex-column gap-8">
                <div className="d-flex flex-column w-100 h-50 justify-content-center align-items-center">
                  <img src={logo} alt="logo" className="w-50 pb-3" />
                  <div className="text-center">
                    <h3 className="p-0 m-0 fs-4">Reset Password</h3>
                    <p className="p-0 m-0 fs-6">Enter your new password</p>
                  </div>
                </div>
                <CardBody>
                  <div className="p-4">
                    {success && <Alert color="success">{success}</Alert>}
                    {error && <Alert color="danger">{error}</Alert>}
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                      action="#"
                    >
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="newPassword">
                          New Password
                        </Label>
                        <InputGroup>
                          <Input
                            name="newPassword"
                            className="form-control"
                            placeholder="Enter New Password"
                            type={showNewPassword ? "text" : "password"}
                            id="newPassword"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.newPassword || ""}
                            invalid={
                              validation.touched.newPassword &&
                              validation.errors.newPassword
                            }
                          />
                          <InputGroupText
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            style={{ cursor: "pointer" }}
                          >
                            {showNewPassword ? <BsEyeSlash /> : <BsEye />}
                          </InputGroupText>
                        </InputGroup>
                        {validation.touched.newPassword &&
                        validation.errors.newPassword ? (
                          <FormFeedback type="invalid">
                            {validation.errors.newPassword}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="confirmPassword">
                          Confirm Password
                        </Label>
                        <InputGroup>
                          <Input
                            name="confirmPassword"
                            className="form-control"
                            placeholder="Confirm Password"
                            type={showConfirmPassword ? "text" : "password"}
                            id="confirmPassword"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.confirmPassword || ""}
                            invalid={
                              validation.touched.confirmPassword &&
                              validation.errors.confirmPassword
                            }
                          />
                          <InputGroupText
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            style={{ cursor: "pointer" }}
                          >
                            {showConfirmPassword ? <BsEyeSlash /> : <BsEye />}
                          </InputGroupText>
                        </InputGroup>
                        {validation.touched.confirmPassword &&
                        validation.errors.confirmPassword ? (
                          <FormFeedback type="invalid">
                            {validation.errors.confirmPassword}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3 row">
                        <div className="text-center">
                          <Button
                            className="btn btn-primary w-md waves-effect waves-light"
                            type="submit"
                            disabled={!email}
                          >
                            Reset Password
                          </Button>
                        </div>
                      </div>
                      <div className="mt-2 mb-0 row text-center">
                        <div className="col-12">
                          <Link to="/login">Back to Login</Link>
                        </div>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ResetPassword;